import React, { useState, useEffect, useRef } from "react";
import {
  FaBuilding,
  FaFingerprint,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaBriefcase,
  FaPercentage,
  FaReceipt,
  FaGlobe,
  FaImage,
  FaSave,
  FaSpinner,
  FaCheckCircle,
  FaListOl,
  FaPlus,
  FaKey,
  FaUniversity,
  FaLock,
  FaStickyNote,
} from "react-icons/fa";
import apiRequest from "../services/api";

const TenantSettings = () => {
  // =========================================================
  // UI STATE
  // =========================================================
  const topRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // =========================================================
  // FORM STATE
  // =========================================================
  const [formData, setFormData] = useState({
    name: "",
    tin: "",
    phone: "",
    vat: "",
    address: "",
    woreda: "",
    industry: "PHARMACY",
    logoUrl: "",
    fiscalType: "MANUAL",
    taxType: "VAT",
    taxRate: 15.0,
    totGoodsRate: 2.0,
    totServiceRate: 10.0,
    withholdRate: 3.0,
    vatWithholdRate: 50.0,
    withholdingAgent: false,
    tax: false,
    timezone: "Africa/Addis_Ababa",

    npgUser: "",
    npgPass: "",
    chapaAccountId: "",
    account: "",
    bank: "",
    expiryNotificationSet: 0,
    backupInterval: 0,
    externalSourceUrl: "",
    letterCode: "",
  });

  // =========================================================
  // REGION STATE
  // =========================================================
  const [regionId, setRegionId] = useState("");
  const [regions, setRegions] = useState([]);

  // =========================================================
  // INPUT CHANGE
  // =========================================================
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // =========================================================
  // REGION CHANGE
  // =========================================================
  const handleRegionChange = (e) => {
    setRegionId(e.target.value);
  };

  // =========================================================
  // LOAD TENANT SETTINGS
  // =========================================================
  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await apiRequest("tenants/me", {
          method: "GET",
        });

        const data = response?.data ?? response ?? {};

        setFormData({
          name: data.name || "",
          tin: data.tin || "",
          phone: data.phone || "",
          vat: data.vat || "",
          address: data.address || "",
          woreda: data.woreda || "",
          industry: data.industry || "PHARMACY",
          logoUrl: data.logoUrl || "",
          fiscalType: data.fiscalType || "MANUAL",
          taxType: data.taxType || "VAT",
          taxRate: data.taxRate ?? 15.0,
          totGoodsRate: data.totGoodsRate ?? 2.0,
          totServiceRate: data.totServiceRate ?? 10.0,
          withholdRate: data.withholdRate ?? 3.0,
          vatWithholdRate: data.vatWithholdRate ?? 50.0,
          withholdingAgent: Boolean(data.withholdingAgent),
          tax: Boolean(data.tax),
          timezone: data.timezone || "Africa/Addis_Ababa",

          npgUser: data.npgUser || "",
          npgPass: data.npgPass || "",
          chapaAccountId: data.chapaAccountId || "",
          account: data.account || "",
          bank: data.bank || "",
          expiryNotificationSet: data.expiryNotificationSet || 0,
          externalSource: data.externalSource || false,
          backupInterval: data.backupInterval || 0,
          externalSourceUrl: data.externalSourceUrl || "",
          letterCode: data.letterCode || "",
        });

        // Load regions
        const regionResponse = await apiRequest("tenants/regions", {
          method: "GET",
        });

        const regionData = regionResponse?.data || regionResponse || [];
        setRegions(regionData);

        // Set current region if available
        setRegionId(data.regionId ? String(data.regionId) : "");
      } catch (err) {
        console.error("Failed to load tenant settings:", err);
        setError(
          err?.response?.message ||
            err?.message ||
            "Failed to load tenant settings."
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  // =========================================================
  // SUBMIT
  // =========================================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setSuccessMsg("");

    if (!formData.name.trim()) {
      setError("Business Name is required.");
      return;
    }
    if (!formData.tin.trim()) {
      setError("TIN is required.");
      return;
    }
    if (!formData.phone.trim()) {
      setError("Telephone is required.");
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        ...formData,
        region: regionId ? { id: Number(regionId) } : null,
      };

      const response = await apiRequest("tenants/update", {
        method: "PUT",
        body: payload,
      });

      if (response?.success === false) {
        throw new Error(response?.message || "Tenant settings update failed.");
      }

      setSuccessMsg("Tenant settings updated successfully.");
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      console.error("Tenant settings update error:", err);
      setError(
        err?.response?.message ||
          err?.message ||
          "Failed to update tenant settings."
      );
    } finally {
      setIsSaving(false);
    }
  };

  // =========================================================
  // STYLES
  // =========================================================
  const inputClass =
    "w-full bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 rounded-xl pl-10 pr-4 py-2.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50 transition";

  const selectClass =
    "w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-xl pl-10 pr-8 py-2.5 text-xs font-sans font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:focus:border-emerald-500/50 transition appearance-none";

  const numberInputClass =
    "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono text-slate-800 text-right focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:focus:border-emerald-500/50 transition";

  const labelClass =
    "block text-[11px] font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase mb-1.5";

  const sectionClass =
    "bg-white border border-slate-200 rounded-2xl p-6 space-y-5 shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl";

  // =========================================================
  // LOADING
  // =========================================================
  if (isLoading) {
    return (
      <div className="min-h-full flex items-center justify-center p-6 bg-slate-50 text-slate-500 dark:bg-slate-950 dark:text-slate-400">
        <div className="flex items-center text-xs">
          <FaSpinner className="animate-spin text-emerald-500 mr-2 text-base" />
          Loading tenant configuration...
        </div>
      </div>
    );
  }

  // =========================================================
  // UI
  // =========================================================
  return (
    <div
      ref={topRef}
      className="min-h-full p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100 relative pb-24"
    >
      <div className="max-w-5xl mx-auto space-y-6 relative z-10">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 p-6 rounded-2xl border backdrop-blur-xl bg-white border-slate-200 shadow-sm dark:bg-slate-900/40 dark:border-white/10 dark:shadow-2xl">
          <div>
            <h1 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-600 text-white shadow-lg shadow-emerald-600/20">
                <FaBuilding />
              </span>
              <span>Business Profile &amp; Tax Rules</span>
            </h1>
            <p className="text-xs mt-2 text-slate-500 dark:text-slate-400">
              Manage business profile, fiscalization, taxation and integrations
            </p>
          </div>
        </div>

        {/* ERROR */}
        {error && (
          <div className="p-4 rounded-xl border bg-red-50 border-red-200 text-red-700 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400 text-xs font-medium">
            {error}
          </div>
        )}

        {/* SUCCESS */}
        {successMsg && (
          <div className="p-4 rounded-xl border bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-400 text-xs font-medium flex items-center gap-2">
            <FaCheckCircle />
            {successMsg}
          </div>
        )}

        {/* FORM */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ===================== BUSINESS INFORMATION ===================== */}
          <div className={sectionClass}>
            <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider border-b border-slate-100 dark:border-white/10 pb-3 flex items-center gap-2">
              <FaBuilding className="text-blue-600 dark:text-blue-400 text-xs" />
              Business Information
            </h2>

            {/* BUSINESS NAME */}
            <div>
              <label className={labelClass}>Business Name *</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                  <FaBuilding className="text-xs" />
                </span>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., ABC PHARMACY PLC"
                  className={inputClass}
                  required
                />
              </div>
            </div>

            {/* TIN / VAT */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>TIN *</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                    <FaFingerprint className="text-xs" />
                  </span>
                  <input
                    type="text"
                    name="tin"
                    value={formData.tin}
                    onChange={handleInputChange}
                    placeholder="e.g., 0012345678"
                    className={inputClass}
                    required
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>VAT Number</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                    <FaPercentage className="text-xs" />
                  </span>
                  <input
                    type="text"
                    name="vat"
                    value={formData.vat}
                    onChange={handleInputChange}
                    placeholder="e.g., 002123456"
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            {/* PHONE / REGION */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Telephone *</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                    <FaPhoneAlt className="text-xs" />
                  </span>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="e.g., +251911223344"
                    className={inputClass}
                    required
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Region</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500 pointer-events-none">
                    <FaMapMarkerAlt className="text-xs" />
                  </span>
                  <select
                    value={regionId}
                    onChange={handleRegionChange}
                    className={selectClass}
                  >
                    <option value="">Select Region</option>
                    {regions.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* WOREDA / INDUSTRY */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Woreda</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                    <FaMapMarkerAlt className="text-xs" />
                  </span>
                  <input
                    type="text"
                    name="woreda"
                    value={formData.woreda}
                    onChange={handleInputChange}
                    placeholder="e.g., 11"
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Industry Type</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                    <FaBriefcase className="text-xs" />
                  </span>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    className={selectClass}
                  >
                    <option value="PHARMACY">Pharmacy / Healthcare</option>
                    <option value="RETAIL">Retail Commerce</option>
                    <option value="WHOLESALE">
                      Distribution &amp; Wholesale
                    </option>
                  </select>
                </div>
              </div>
            </div>

            {/* ADDRESS */}
            <div>
              <label className={labelClass}>Office Address</label>
              <div className="relative">
                <span className="absolute top-3 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                  <FaMapMarkerAlt className="text-xs" />
                </span>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows="2"
                  placeholder="e.g., Bole Sub-City, Woreda 03, Addis Ababa"
                  className={`${inputClass} resize-none`}
                />
              </div>
            </div>

            {/* LOGO URL */}
            <div>
              <label className={labelClass}>Logo Link</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                  <FaImage className="text-xs" />
                </span>
                <input
                  type="text"
                  name="logoUrl"
                  value={formData.logoUrl}
                  onChange={handleInputChange}
                  placeholder="e.g., https://abc.com/logo.png"
                  className={inputClass}
                />
              </div>
            </div>

            {/* ADDRESS */}
            <div>
              <label className={labelClass}>Memo Refference Prefix</label>
              <div className="relative">
                <span className="absolute top-3 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                  <FaStickyNote className="text-xs" />
                </span>
                <input
                  name="letterCode"
                  value={formData.letterCode}
                  onChange={handleInputChange}
                  placeholder="e.g., Muy/Mem"
                  className={`${inputClass} resize-none`}
                />
              </div>
            </div>
          </div>

          {/* ===================== FISCALIZATION ===================== */}
          <div className={sectionClass}>
            <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider border-b border-slate-100 dark:border-white/10 pb-3 flex items-center gap-2">
              <FaCheckCircle className="text-emerald-600 dark:text-emerald-400 text-xs" />
              Fiscalization
            </h2>

            <div>
              <label className={labelClass}>Fiscalization Mode</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500 pointer-events-none">
                  <FaReceipt className="text-xs" />
                </span>
                <select
                  name="fiscalType"
                  value={formData.fiscalType}
                  onChange={handleInputChange}
                  className={selectClass}
                >
                  <option value="MANUAL">Manual</option>
                  <option value="MACHINE">Machine</option>
                  <option value="EIMS">EIMS</option>
                </select>
              </div>
            </div>
          </div>

          {/* ===================== TAX SETTINGS ===================== */}
          <div className={sectionClass}>
            <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider border-b border-slate-100 dark:border-white/10 pb-3 flex items-center gap-2">
              <FaPercentage className="text-emerald-600 dark:text-emerald-400 text-xs" />
              Tax Settings
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Tax Scheme Type</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500 pointer-events-none">
                    <FaPercentage className="text-xs" />
                  </span>
                  <select
                    name="taxType"
                    value={formData.taxType}
                    onChange={handleInputChange}
                    className={selectClass}
                  >
                    <option value="VAT">VAT (Value Added Tax)</option>
                    <option value="TOT">TOT (Turnover Tax)</option>
                    <option value="NONE">Exempt / Non-registered</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-6">
                <input
                  type="checkbox"
                  name="tax"
                  checked={formData.tax}
                  onChange={handleInputChange}
                  className="w-4 h-4 accent-emerald-500"
                />
                <span className="text-slate-600 dark:text-slate-300 text-xs">
                  Apply Tax on invoices
                </span>
              </div>

              <div className="flex items-center gap-3 pt-6">
                <input
                  type="checkbox"
                  name="withholdingAgent"
                  checked={formData.withholdingAgent}
                  onChange={handleInputChange}
                  className="w-4 h-4 accent-emerald-500"
                />
                <span className="text-slate-600 dark:text-slate-300 text-xs">
                  Apply withholding on invoices
                </span>
              </div>

              <div>
                <label className={labelClass}>Timezone</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500 pointer-events-none">
                    <FaGlobe className="text-xs" />
                  </span>
                  <select
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleInputChange}
                    className={selectClass}
                  >
                    <option value="Africa/Addis_Ababa">
                      Africa/Addis_Ababa (EAT +03:00)
                    </option>
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">America/New_York</option>
                    <option value="Europe/London">Europe/London</option>
                  </select>
                </div>
              </div>
            </div>

            {formData.taxType === "VAT" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 bg-slate-50 border border-slate-200 p-4 rounded-xl dark:bg-slate-950/50 dark:border-white/10">
                <div>
                  <label className={labelClass}>VAT Rate (%)</label>
                  <input
                    type="number"
                    name="taxRate"
                    step="0.01"
                    min="0"
                    value={formData.taxRate}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        taxRate: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className={numberInputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Withholding Rate (%)</label>
                  <input
                    type="number"
                    name="withholdRate"
                    step="0.01"
                    min="0"
                    value={formData.withholdRate}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        withholdRate: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className={numberInputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>VAT Withhold Rate (%)</label>
                  <input
                    type="number"
                    name="vatWithholdRate"
                    step="0.01"
                    min="0"
                    value={formData.vatWithholdRate}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        vatWithholdRate: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className={numberInputClass}
                  />
                </div>
              </div>
            )}

            {formData.taxType === "TOT" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 border border-slate-200 p-4 rounded-xl dark:bg-slate-950/50 dark:border-white/10">
                <div>
                  <label className={labelClass}>TOT Goods Rate (%)</label>
                  <input
                    type="number"
                    name="totGoodsRate"
                    step="0.01"
                    min="0"
                    value={formData.totGoodsRate}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        totGoodsRate: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className={numberInputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>TOT Service Rate (%)</label>
                  <input
                    type="number"
                    name="totServiceRate"
                    step="0.01"
                    min="0"
                    value={formData.totServiceRate}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        totServiceRate: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className={numberInputClass}
                  />
                </div>
              </div>
            )}
          </div>

          {/* ===================== INTEGRATIONS ===================== */}
          <div className={sectionClass}>
            <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider border-b border-slate-100 dark:border-white/10 pb-3 flex items-center gap-2">
              <FaKey className="text-amber-600 dark:text-amber-400 text-xs" />
              Integrations & Credentials
            </h2>

            {/* NPG */}
            <div className="space-y-4 pt-4 border-t border-slate-100 dark:border-white/10">
              <h3 className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                NPG Credentials
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>NPG User</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                      <FaKey className="text-xs" />
                    </span>
                    <input
                      type="text"
                      name="npgUser"
                      value={formData.npgUser}
                      onChange={handleInputChange}
                      placeholder="NPG Username"
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>NPG Password</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                      <FaLock className="text-xs" />
                    </span>
                    <input
                      type="password"
                      name="npgPass"
                      value={formData.npgPass}
                      onChange={handleInputChange}
                      placeholder="NPG Password"
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bank / Chapa */}
            <div className="space-y-4 pt-4 border-t border-slate-100 dark:border-white/10">
              <h3 className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                Bank & Payment (for wallet based online collections)
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Bank Name</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                      <FaUniversity className="text-xs" />
                    </span>
                    <input
                      type="text"
                      name="bank"
                      value={formData.bank}
                      onChange={handleInputChange}
                      placeholder="e.g. Commercial Bank of Ethiopia"
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Account Number</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                      <FaUniversity className="text-xs" />
                    </span>
                    <input
                      type="text"
                      name="account"
                      value={formData.account}
                      onChange={handleInputChange}
                      placeholder="Account Number"
                      className={inputClass}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>
                    Account ID (Chapa Portal)
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                      <FaKey className="text-xs" />
                    </span>
                    <input
                      type="text"
                      name="chapaAccountId"
                      value={formData.chapaAccountId}
                      onChange={handleInputChange}
                      placeholder="Chapa Account ID"
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="sm:col-span-2 flex flex-col gap-4">
            {/* Expiry Notification Period */}
            <div>
              <label className={labelClass}>
                Expiry Notification Period (Days Before Expiry)
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                  <FaKey className="text-xs" />
                </span>
                <input
                  type="number"
                  name="expiryNotificationSet"
                  value={formData.expiryNotificationSet}
                  onChange={handleInputChange}
                  placeholder="#Days Before Expiry"
                  className={inputClass}
                />
              </div>
            </div>

            {/* Backup Interval */}
            <div>
              <label className={labelClass}>Backup Intervals (#Days)</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                  <FaKey className="text-xs" />
                </span>
                <input
                  type="number"
                  name="backupInterval"
                  value={formData.backupInterval}
                  onChange={handleInputChange}
                  placeholder="Days of backup interval"
                  className={inputClass}
                />
              </div>
            </div>

            {/* External Source Toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                name="externalSource"
                checked={!!formData.externalSource}
                onChange={handleInputChange}
                className="w-4 h-4 accent-emerald-500"
              />
              <label className="text-slate-600 dark:text-slate-300 text-xs">
                Use external source for import
              </label>
            </div>

            {/* External Source URL */}
            <div>
              <label className={labelClass}>External Source Address</label>
              <input
                type="text"
                name="externalSourceUrl"
                value={formData.externalSourceUrl || ""}
                onChange={handleInputChange}
                placeholder="https://sobekg.nvs-clouderp.com.et"
                disabled={!formData.externalSource}
                className={inputClass}
              />
            </div>
          </div>

          {/* ===================== SAVE ===================== */}
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center justify-center gap-2 px-7 py-3 bg-emerald-600 hover:bg-emerald-700 active:scale-95 disabled:opacity-50 disabled:pointer-events-none text-white font-bold rounded-xl text-xs transition shadow-lg shadow-emerald-600/20 uppercase tracking-wider"
            >
              {isSaving ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TenantSettings;
